import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Volumenrechner from "@/pages/Volumenrechner";
import { PhotoUpload, photoUrl } from "@/components/PhotoUpload";
import { RouteMap } from "@/components/RouteMap";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, MapPin, Package, Ruler, Building2, Calendar, Wrench, FileText } from "lucide-react";
import { PromoBanner } from "@/components/PromoBanner";
import { useAuth } from "@/context/AuthContext";

const CARGO = ["Umzugsgut","Möbel","Einzelmöbel","Sofa","Schrank","Bett","Küche","Kartons","Haushaltsgeräte","Sonstiges"];
const FLOORS = ["EG","1. OG","2. OG","3. OG","höher"];
const ACCESS = ["Aufzug vorhanden","kein Aufzug","Möbellift erforderlich","Halteverbotszone erforderlich"];
const SERVICES = ["Tragen","Einpacken","Möbelmontage","Möbel demontieren","Möbellift","Verpackungsmaterial","Zwischenlagerung"];

const STEPS = [
  { icon: MapPin, label: "Startort" }, { icon: MapPin, label: "Zielort" },
  { icon: Package, label: "Transportgut" }, { icon: Ruler, label: "Volumen" },
  { icon: Building2, label: "Zugang" }, { icon: Calendar, label: "Termin" },
  { icon: Wrench, label: "Zusatzleistungen" }, { icon: FileText, label: "Zusammenfassung" },
];

export default function RequestWizard() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    start: { street: "", plz: "", ort: params.get("von") || "", land: "Deutschland" },
    dest: { street: "", plz: "", ort: params.get("nach") || "", land: "Deutschland" },
    cargo_type: params.get("cargo") || "Umzugsgut", items: [],
    volume_m3: params.get("volumen") ? parseFloat(params.get("volumen")) : 0,
    access_start: "EG", access_dest: "EG", access_options: [],
    date_from: params.get("from") || "", date_to: params.get("to") || "",
    flexible: params.get("flexible") === "1", services: [],
    guest_name: "", guest_email: "", guest_phone: "", photos: [],
  });

  useEffect(() => {
    const raw = params.get("items");
    if (raw) { try { setData((d) => ({ ...d, items: JSON.parse(decodeURIComponent(raw)) })); } catch {} }
  }, []); // eslint-disable-line

  const setStart = (k) => (e) => setData({ ...data, start: { ...data.start, [k]: e.target.value } });
  const setDest = (k) => (e) => setData({ ...data, dest: { ...data.dest, [k]: e.target.value } });
  const toggle = (field, val) => setData((d) => ({
    ...d, [field]: d[field].includes(val) ? d[field].filter((x) => x !== val) : [...d[field], val],
  }));

  const canNext = () => {
    if (step === 0) return data.start.ort || data.start.plz;
    if (step === 1) return data.dest.ort || data.dest.plz;
    return true;
  };

  const submit = async () => {
    if (!user && (!data.guest_name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.guest_email.trim()))) {
      toast.error("Bitte Name und eine gültige E-Mail-Adresse angeben."); return;
    }
    setLoading(true);
    try {
      const { data: res } = await api.post("/requests", data);
      toast.success(`Anfrage erstellt – ${res.offers_count} passende Beiladung(en) gefunden.`);
      nav(`/angebote/${res.request.id}${res.access_token ? `?token=${res.access_token}` : ""}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="eb-container py-10 max-w-4xl">
      <h1 className="font-head text-2xl lg:text-3xl font-bold mb-4">Transportanfrage</h1>
      <div className="mb-6"><PromoBanner compact /></div>

      {/* Stepper */}
      <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2" data-testid="wizard-stepper">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center shrink-0">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${
              i === step ? "bg-[#FF5A1F] text-white" : i < step ? "text-emerald-400" : "text-slate-500"}`}>
              {i < step ? <Check className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />} {s.label}
            </div>
            {i < STEPS.length - 1 && <div className="w-3 h-px bg-slate-700 mx-0.5" />}
          </div>
        ))}
      </div>

      <div className="eb-card p-6 lg:p-8 min-h-[340px]" data-testid={`wizard-step-${step}`}>
        {step === 0 && <AddressStep title="Wo soll abgeholt werden?" addr={data.start} set={setStart} floor={data.access_start} onFloor={(v)=>setData({...data,access_start:v})} prefix="start" />}
        {step === 1 && <AddressStep title="Wohin soll transportiert werden?" addr={data.dest} set={setDest} floor={data.access_dest} onFloor={(v)=>setData({...data,access_dest:v})} prefix="dest" />}
        {step === 2 && (
          <div>
            <h3 className="font-head text-xl font-semibold mb-4">Was soll transportiert werden?</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {CARGO.map((c) => (
                <button key={c} onClick={() => setData({ ...data, cargo_type: c })} data-testid={`cargo-${c}`}
                  className={`px-3 py-3 rounded-lg border text-sm transition-colors ${data.cargo_type===c?"border-[#FF5A1F] bg-[#FF5A1F]/10 text-white":"border-slate-800 text-slate-300 hover:border-slate-600"}`}>{c}</button>
              ))}
            </div>
            <div className="mt-6 border-t border-slate-800 pt-5">
              <PhotoUpload photos={data.photos} onChange={(photos) => setData({ ...data, photos })} />
            </div>
          </div>
        )}
        {step === 3 && (
          <div>
            <h3 className="font-head text-xl font-semibold mb-1">Volumen</h3>
            <p className="text-sm text-slate-400 mb-4">Gib dein Volumen direkt an oder nutze den Volumenrechner.</p>
            <div className="flex items-end gap-3 mb-6">
              <div className="flex-1"><label className="eb-label">Volumen (m³)</label>
                <input type="number" step="0.5" className="eb-input mt-1.5" value={data.volume_m3}
                  onChange={(e) => setData({ ...data, volume_m3: parseFloat(e.target.value) || 0 })} data-testid="wizard-volume-input" /></div>
            </div>
            <div className="border-t border-slate-800 pt-4">
              <Volumenrechner embedded onResult={(t, items) => { setData({ ...data, volume_m3: t, items }); toast.success(`${t} m³ übernommen`); }} />
            </div>
          </div>
        )}
        {step === 4 && (
          <div>
            <h3 className="font-head text-xl font-semibold mb-4">Zugangssituation</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              <div><label className="eb-label">Abholort – Stockwerk</label>
                <select className="eb-input mt-1.5" value={data.access_start} onChange={(e)=>setData({...data,access_start:e.target.value})} data-testid="access-start-floor">{FLOORS.map(f=><option key={f}>{f}</option>)}</select></div>
              <div><label className="eb-label">Zielort – Stockwerk</label>
                <select className="eb-input mt-1.5" value={data.access_dest} onChange={(e)=>setData({...data,access_dest:e.target.value})} data-testid="access-dest-floor">{FLOORS.map(f=><option key={f}>{f}</option>)}</select></div>
            </div>
            <label className="eb-label mt-6 block">Weitere Angaben</label>
            <div className="grid sm:grid-cols-2 gap-2.5 mt-2">
              {ACCESS.map((a) => (
                <button key={a} onClick={() => toggle("access_options", a)} data-testid={`access-${a.slice(0,6)}`}
                  className={`px-3 py-2.5 rounded-lg border text-sm text-left transition-colors ${data.access_options.includes(a)?"border-[#FF5A1F] bg-[#FF5A1F]/10 text-white":"border-slate-800 text-slate-300 hover:border-slate-600"}`}>{a}</button>
              ))}
            </div>
          </div>
        )}
        {step === 5 && (
          <div>
            <h3 className="font-head text-xl font-semibold mb-4">Termin</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="eb-label">Frühester Termin</label><input type="date" className="eb-input mt-1.5" value={data.date_from} onChange={(e)=>setData({...data,date_from:e.target.value})} data-testid="wizard-date-from" /></div>
              <div><label className="eb-label">Spätester Termin</label><input type="date" className="eb-input mt-1.5" value={data.date_to} onChange={(e)=>setData({...data,date_to:e.target.value})} data-testid="wizard-date-to" /></div>
            </div>
            <label className="flex items-center gap-2.5 text-sm text-slate-300 mt-5 cursor-pointer">
              <input type="checkbox" checked={data.flexible} onChange={(e)=>setData({...data,flexible:e.target.checked})} className="w-4 h-4 accent-[#FF5A1F]" data-testid="wizard-flexible" />
              Ich bin beim Termin flexibel (bessere Matches & Preise)
            </label>
          </div>
        )}
        {step === 6 && (
          <div>
            <h3 className="font-head text-xl font-semibold mb-4">Zusatzleistungen</h3>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {SERVICES.map((s) => (
                <button key={s} onClick={() => toggle("services", s)} data-testid={`service-${s.slice(0,6)}`}
                  className={`px-3 py-2.5 rounded-lg border text-sm text-left transition-colors ${data.services.includes(s)?"border-[#FF5A1F] bg-[#FF5A1F]/10 text-white":"border-slate-800 text-slate-300 hover:border-slate-600"}`}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {step === 7 && (
          <div>
            <h3 className="font-head text-xl font-semibold mb-4">Zusammenfassung</h3>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Sum label="Von" value={`${data.start.plz} ${data.start.ort} (${data.access_start})`} />
              <Sum label="Nach" value={`${data.dest.plz} ${data.dest.ort} (${data.access_dest})`} />
              <Sum label="Transportgut" value={data.cargo_type} />
              <Sum label="Volumen" value={`${data.volume_m3} m³`} />
              <Sum label="Termin" value={data.flexible ? "Flexibel" : `${data.date_from || "–"} bis ${data.date_to || "–"}`} />
              <Sum label="Zusatzleistungen" value={data.services.join(", ") || "keine"} />
              <Sum label="Fotos" value={data.photos.length ? `${data.photos.length} hochgeladen` : "keine"} />
            </div>
            {data.photos.length > 0 && (
              <div className="flex gap-2 mt-3">{data.photos.map((id) => <img key={id} src={photoUrl(id)} alt="" className="w-14 h-14 rounded object-cover border border-slate-800" />)}</div>
            )}
            <div className="mt-5"><RouteMap von={data.start.ort} vonPlz={data.start.plz} nach={data.dest.ort} nachPlz={data.dest.plz} /></div>
            <div className="border-t border-slate-800 mt-6 pt-6">
              <label className="eb-label mb-2 block">{user ? "Kontaktdaten (aus deinem Profil)" : "Kontaktdaten (Pflicht – damit wir dich erreichen können)"}</label>
              {user ? (
                <div className="text-sm text-slate-300" data-testid="contact-from-profile">{user.name} · {user.email}{user.phone ? ` · ${user.phone}` : ""}</div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-3">
                  <input className="eb-input" placeholder="Name *" required value={data.guest_name} onChange={(e)=>setData({...data,guest_name:e.target.value})} data-testid="guest-name" />
                  <input className="eb-input" type="email" placeholder="E-Mail *" required value={data.guest_email} onChange={(e)=>setData({...data,guest_email:e.target.value})} data-testid="guest-email" />
                  <input className="eb-input" placeholder="Telefon (optional)" value={data.guest_phone} onChange={(e)=>setData({...data,guest_phone:e.target.value})} data-testid="guest-phone" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="eb-btn-outline" data-testid="wizard-back-btn">
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>
        {step < 7 ? (
          <button onClick={() => canNext() ? setStep(step + 1) : toast.error("Bitte Ort oder PLZ angeben")} className="eb-btn-primary" data-testid="wizard-next-btn">
            Weiter <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={loading} className="eb-btn-primary" data-testid="wizard-submit-btn">
            {loading ? "Suche…" : "Passende Beiladungen finden"} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function AddressStep({ title, addr, set, floor, onFloor, prefix }) {
  return (
    <div>
      <h3 className="font-head text-xl font-semibold mb-4">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><label className="eb-label">Straße (optional)</label><input className="eb-input mt-1.5" value={addr.street} onChange={set("street")} data-testid={`${prefix}-street`} /></div>
        <div><label className="eb-label">PLZ</label><input className="eb-input mt-1.5" value={addr.plz} onChange={set("plz")} data-testid={`${prefix}-plz`} /></div>
        <div><label className="eb-label">Ort</label><input className="eb-input mt-1.5" value={addr.ort} onChange={set("ort")} data-testid={`${prefix}-ort`} /></div>
        <div><label className="eb-label">Land</label><input className="eb-input mt-1.5" value={addr.land} onChange={set("land")} data-testid={`${prefix}-land`} /></div>
      </div>
    </div>
  );
}
function Sum({ label, value }) {
  return <div className="rounded-lg border border-slate-800 p-3"><div className="eb-label">{label}</div><div className="text-slate-200 mt-1">{value}</div></div>;
}
